import { yupResolver } from "@hookform/resolvers/yup"
import { IbanElement, useElements, useStripe } from "@stripe/react-stripe-js"
import type { StripeIbanElementChangeEvent } from "@stripe/stripe-js"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { captureException, captureMessage } from "@sentry/react"
import * as Yup from "yup"
import { useEffectOnce } from "@oysterhr/common"

import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import useCompanyAddress from "~/app/pages/payments/hooks/useCompanyAddress"
import useCreateSepaPaymentMethod from "./useCreateSepaPaymentMethod"
import useCreateSepaSetupIntent from "./useCreateSepaSetupIntent"
import { useToast, Toast } from "@oysterhr/ui"

import { AddSepaFormValues, useAddStripeSepaFormProps } from "~/app/pages/payments/types"
import { useConfiguredToasts } from "@oysterhr/feature-components"

const useAddStripeSepaForm = ({ nextStep }: useAddStripeSepaFormProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage")
  const toast = useToast()
  const stripe = useStripe()
  const elements = useElements()
  const { displayErrorToast } = useConfiguredToasts()
  const [confirmingSepaDirectDebit, setConfirmingSepaDirectDebit] = useState(false)
  const [createSepaPaymentMethod, { loading }] = useCreateSepaPaymentMethod()
  const [createSepaSetupIntent, { data: sepaSetupIntentData }] = useCreateSepaSetupIntent()
  const { accountAspect: companyId } = useAccountAspect()
  const { data: companyData, loading: companyLoading } = useCompanyAddress({
    variables: { companyId },
  })

  const stripeClientSecret = sepaSetupIntentData?.createSepaSetupIntent.setupIntent?.clientSecret

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(t("validation.required", { field: t("modal.accountHolderName") }))
          .min(3, t("validation.accountHolderName"))
          .max(100, t("validation.accountHolderName")),
        email: Yup.string()
          .email()
          .required(t("validation.required", { field: t("modal.email") })),
        iban: Yup.string().required(t("validation.required", { field: t("modal.iban") })),
      }),
    [t],
  )

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      iban: "",
    },
    resolver: yupResolver(validationSchema),
  })

  useEffectOnce(() => {
    const effect = async () => {
      try {
        await createSepaSetupIntent({
          variables: { companyId: companyId },
          onCompleted: data => {
            if (data.createSepaSetupIntent.errors.length) {
              toast({
                render: (
                  <Toast variation="error">
                    <Toast.Content>{data.createSepaSetupIntent.errors.join(", ")}</Toast.Content>
                  </Toast>
                ),
              })
            }
          },
        })
      } catch (err) {
        captureException(err)
      }
    }

    effect()
  })

  const getSepaDebitSetupIntent = async (values: AddSepaFormValues) => {
    if (!stripe || !stripeClientSecret || !elements) {
      throw new Error("Stripe isn't ready")
    }

    const company = companyData?.company
    const ibanElement = elements?.getElement(IbanElement)
    const address = company?.billingAddress || company?.address

    if (!ibanElement) {
      throw new Error("No IBAN element")
    }
    setConfirmingSepaDirectDebit(true)
    const { setupIntent, error } = await stripe.confirmSepaDebitSetup(stripeClientSecret, {
      payment_method: {
        sepa_debit: ibanElement,
        billing_details: {
          name: values.name,
          email: values.email,
          address: {
            line1: address?.addressLine1,
            line2: address?.addressLine2,
            postal_code: address?.postalCode,
            country: address?.countryCode,
            city: address?.city,
            state: address?.region,
          },
        },
      },
    })
    setConfirmingSepaDirectDebit(false)

    if (error) {
      captureMessage(error.message || "Couldn't submit SEPA direct debit details", {
        extra: {
          ...error,
        },
      })
      throw new Error(error.message)
    }

    return setupIntent
  }

  const onIbanChange = (event: StripeIbanElementChangeEvent) => {
    const newValue = event.empty ? "" : "iban"
    const error = event.error || {
      message: t("validation.required", { field: t("modal.iban") }),
    }
    const newError = event.empty || event.error ? error : {}

    form.setValue(event.elementType, newValue)
    form.setError(event.elementType, newError)
  }

  const onSubmit = async (values: AddSepaFormValues) => {
    try {
      const setupIntent = await getSepaDebitSetupIntent(values)

      await createSepaPaymentMethod({
        variables: { ownerId: companyId, setupIntentId: setupIntent?.id },
        onCompleted: data => {
          const { errors } = data.createPaymentMethodStripeSepa

          if (errors.length) {
            throw new Error(errors.join(", "))
          } else {
            nextStep()
          }
        },
      })
    } catch (err) {
      displayErrorToast({
        content: (err as Error).message,
      })
      console.error(err)
      throw err
    }
  }

  return {
    ...form,
    loading: loading || confirmingSepaDirectDebit,
    onSubmit,
    onIbanChange,
    formIsReady: !!stripe && !companyLoading && !!stripeClientSecret,
  }
}

export default useAddStripeSepaForm
